import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  displayListDetailsTransformer,
  IRecentPurchaseState,
  LIST_ACTIONS,
  ListConstants,
  ListTracingConstants,
  ListType,
  ListViewMode,
  ListViewService,
  selectRecentPurchaseState,
} from '@usf/ngrx-list';
import { ProductPricingService } from '@usf/ngrx-pricing';
import { ProductStateService, getProductAlternates } from '@usf/ngrx-product';
import { Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { State } from 'src/app/store';
import { PATHS } from '../../../../shared/constants/paths';
import { NavigationHelperService } from '../../../../shared/helpers/navigation.helpers.service';
import { Product } from '../../../../shared/models/product.model';
import { productsAreInState } from '../../../../shared/selectors/helpers/product-info.selectors.helper';
import { ProductService } from '../../../../shared/services/product/product.service';
import { ListDetailManagementService } from '../../../shared/list-detail-management/services/list-detail-management.service';
import { Resolve } from '@angular/router';
import {
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
} from '../../../../shared/constants/newrelic-customattribute-constants';

@Injectable({
  providedIn: 'root',
})
export class RecentlyPurchasedResolverService
  implements Resolve<Observable<any>>
{
  loading = false;
  constructor(
    private store: Store<State>,
    private productService: ProductService,
    private navigationHelperService: NavigationHelperService,
    private productPricingService: ProductPricingService,
    private productStateService: ProductStateService,
    private listDetailManagementService: ListDetailManagementService,
    private readonly listViewService: ListViewService,
  ) {}

  resolve() {
    return this.store.select(selectRecentPurchaseState).pipe(
      filter(
        (recentPurchaseState: IRecentPurchaseState) =>
          recentPurchaseState.loaded,
      ),
      take(1),
      tap((recentPurchaseState: IRecentPurchaseState) => {
        if (recentPurchaseState.ids.length > 0) {
          this.listDetailManagementService.displayListDetailsStartTrace(
            ListConstants.recentlyPurchased,
            '0',
            recentPurchaseState.ids.length,
          );

          const newrelic = window['newrelic'];

          try {
            if (!!newrelic) {
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
                '0',
              );
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
                ListConstants.recentlyPurchased,
              );
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
                recentPurchaseState.ids.length,
              );
            }
          } catch (err) {
            console.log(
              'Error in open recently purchased list detail new relic event',
            );
            console.log(err);
          }
          this.listViewService.selectList(
            null,
            ListType.recentPurchase,
            ListViewMode.ordering,
          );
          this.getProductAndPricingData(recentPurchaseState);
        } else {
          this.navigationHelperService.routeTo(PATHS.HOME);
        }
      }),
    );
  }

  getProductAndPricingData(recentPurchaseState: IRecentPurchaseState) {
    const productNumbers: number[] = [];
    recentPurchaseState.ids.forEach(recentPurchaseId => {
      productNumbers.push(Number(recentPurchaseId));
    });
    this.productStateService.loadProductsWithRetry(productNumbers);
    this.productPricingService.loadPrices(productNumbers);
    // Select products we loaded so that we can get their alternates
    this.productService
      .getProducts(productNumbers)
      .pipe(filter(products => productsAreInState(products)))
      .pipe(take(1))
      .subscribe(products => {
        // Convert product map to array
        const productMapToArray = Array.from(products.values()).reduce(
          (list, product) => {
            list.push({
              productNumber: product?.productNumber,
              summary: product?.summary,
              inventory: product?.inventory,
            } as Product);
            return list;
          },
          new Array<Product>(),
        );
        const alternateProductNumbers = getProductAlternates(productMapToArray);
        this.productPricingService.loadPrices(alternateProductNumbers);
      });
  }
}
